/* @flow */
import * as React from 'react';
import Button from 'components/Button';
import { getGlyphName } from 'lib/FileTypes';
import Glyph from 'components/Glyph';
import Image from 'components/Image';
import Link from 'components/Link';
import File from 'entities/File';
import ModalTrigger from 'components/ModalTrigger';
import ImageFilePreviewModal from 'components/ImageFilePreviewModal';
import cx from 'classnames';
import './styles/ComposerFilePreview.scss';

type Props = {
  progress: number,
  thumbnail?: string,
  type?: ?string,
  onDelete?: Function,
  onRetry?: Function,
  error: boolean,
  file?: ?File,
  showUploadingPercentage?: boolean,
};

class ComposerFilePreview extends React.Component<Props> {
  static defaultProps = { progress: 0, error: false };

  // If only I could put quotes around "Progress"
  getProgress = () => {
    if (this.props.error) {
      return 0;
    }
    const percent = (this.props.progress / 100) ** 0.7;
    return Math.floor(percent * 100);
  };

  // When editing an announcement that has an image attachement,
  // we don't have the thumbnail that we usually have during uploading,
  // but we can display the image itself as a pseudo-thumbnail
  isFileThumbnail = () => {
    const { file } = this.props;
    return file && Image.isSupportedType(file.getContentType());
  };

  renderPreview = () => {
    const { file } = this.props;

    let preview = null;
    if (this.props.thumbnail) {
      preview = <Image className="ComposerFilePreview-image" src={this.props.thumbnail} />;
    } else if (file && this.isFileThumbnail()) {
      preview = (
        <div
          className={cx('ComposerFilePreview-image', 'ComposerFilePreview-image--background')}
          style={{ background: `url("${file.getUrls().cdn}")` }}
        />
      );
    } else {
      preview = (
        <Glyph
          className="ComposerFilePreview-glyph"
          name={getGlyphName(this.props.type)}
          size="xlarge"
          solo
        />
      );
    }

    if (file) {
      const url = file.getUrls().cdn;
      if (Image.isSupportedType(file.getContentType())) {
        const modal = <ImageFilePreviewModal file={file.toGraphqlType()} />;
        preview = (
          <ModalTrigger modal={modal}>
            <Link
              className="MessageItemAttachment-image"
              href={url}
              target="_blank"
              eventElementName="MessageItemAttachment-image">
              {preview}
            </Link>
          </ModalTrigger>
        );
      } else {
        preview = (
          <Link
            className="MessageItemAttachment-icon"
            href={url}
            target="_blank"
            eventElementName="MessageItemAttachment-icon">
            {preview}
          </Link>
        );
      }
    }

    return preview;
  };

  renderStatus = () => {
    const { error, progress, showUploadingPercentage, onDelete, onRetry } = this.props;
    if (error) {
      return (
        <div>
          <Glyph name="caution" color="lava" /> Upload error.{' '}
          <Link color="inherit" weight="semibold" onClick={onRetry}>
            Retry
          </Link>{' '}
          or{' '}
          <Link color="inherit" weight="semibold" onClick={onDelete}>
            Cancel
          </Link>
          ?
        </div>
      );
    }
    if (progress < 100) {
      return (
        <div>
          {showUploadingPercentage && (
            <span className="ComposerFilePreview-progress">Uploading… {this.getProgress()}%</span>
          )}
          <Button
            className="ComposerFilePreview-cancelReduxUploadButton"
            level="text"
            color="brand"
            onClick={onDelete}
            aria-label="Cancel"
            eventElementName="ComposerFilePreview-cancelReduxUploadButton">
            <Glyph name="close-circle" size="large" solo />
          </Button>
        </div>
      );
    } else if (onDelete) {
      return (
        <Button
          className="ComposerFilePreview-cancelAttachButton"
          level="text"
          color="brand"
          onClick={onDelete}
          aria-label="Cancel"
          eventElementName="ComposerFilePreview-cancelAttachButton">
          <Glyph name="close-circle" size="xlarge" solo />
        </Button>
      );
    }
    return null;
  };
  render() {
    const preview = this.renderPreview();
    const status = this.renderStatus();
    return (
      <div className="ComposerFilePreview">
        <div
          className={cx({
            'ComposerFilePreview-preview': true,
            'ComposerFilePreview-previewNoThumbnail': !(
              this.props.thumbnail || this.isFileThumbnail()
            ),
          })}>
          {preview}
          <div
            className="ComposerFilePreview-mask"
            style={{
              width: 100 - this.getProgress() + '%',
            }}
          />
        </div>
        <div className="ComposerFilePreview-status">{status}</div>
      </div>
    );
  }
}
export default ComposerFilePreview;
